import Vue from 'vue';
import { createInterfaceSound, playInterfaceSound } from '@/service/interfaceSound';

// Create audio element for the notification sound
const notificationSound = createInterfaceSound('/sound/pikpik.wav');

// Function to play notification sound
const playNotificationSound = () => {
    playInterfaceSound(notificationSound).catch(error => {
        // Silent error handling
    });
};

// Function to check if element is a notification
const isNotificationElement = (element: Element): boolean => {
    // Check for various possible notification class names
    const possibleClasses = [
        'vt-notification-container',
        'vt-container',
        'vt-notification',
        'vue-toastify',
        'toastify',
        'notification-container',
        'toast-container'
    ];
    
    for (const className of possibleClasses) {
        if (element.classList && element.classList.contains(className)) {
            return true;
        }
    }
    
    // Also check for elements with vt- prefix
    if (element.classList) {
        for (const className of element.classList) {
            if (className.startsWith('vt-')) {
                return true;
            }
        }
    }
    
    return false;
};

// Wait for Vue to be ready and override $vToastify methods
Vue.nextTick(() => {
    // Get the original vue-toastify instance
    const { $vToastify: originalToastify } = new Vue() as any;
    
    if (originalToastify) {
        // Create a wrapper that plays sound when notifications are shown
        const notificationSoundWrapper = {
            success: (options: any) => {
                playNotificationSound();
                return originalToastify.success(options);
            },
            error: (options: any) => {
                playNotificationSound();
                return originalToastify.error(options);
            },
            info: (options: any) => {
                playNotificationSound();
                return originalToastify.info(options);
            },
            warning: (options: any) => {
                playNotificationSound();
                return originalToastify.warning(options);
            }
        };

        // Override the global $vToastify with our sound-enabled wrapper
        Vue.prototype.$vToastify = notificationSoundWrapper;
        
        // Also override the global toastify instance
        (window as any).$vToastify = notificationSoundWrapper;
    }
});

// Wait for DOM to be ready
document.addEventListener('DOMContentLoaded', () => {
    // Create a MutationObserver to watch for notification containers
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            // Check for added nodes
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const element = node as Element;
                    
                    // Check if the added element is a notification container
                    if (isNotificationElement(element)) {
                        playNotificationSound();
                        return;
                    }
                    
                    // Also check if any child elements contain notification containers
                    const allElements = element.querySelectorAll('*');
                    for (const childElement of allElements) {
                        if (isNotificationElement(childElement)) {
                            playNotificationSound();
                            return;
                        }
                    }
                }
            });
        });
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
});

// Also check if DOM is already loaded
if (document.readyState === 'loading') {
    // DOM is still loading, waiting for DOMContentLoaded...
} else {
    // DOM is already loaded, set up observer immediately
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            // Check for added nodes
            mutation.addedNodes.forEach((node) => {
                if (node.nodeType === Node.ELEMENT_NODE) {
                    const element = node as Element;
                    
                    // Check if the added element is a notification container
                    if (isNotificationElement(element)) {
                        playNotificationSound();
                        return;
                    }
                    
                    // Also check if any child elements contain notification containers
                    const allElements = element.querySelectorAll('*');
                    for (const childElement of allElements) {
                        if (isNotificationElement(childElement)) {
                            playNotificationSound();
                            return;
                        }
                    }
                }
            });
        });
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
} 
